import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { formatPersonName } from '../utils/formatName';
import { roleLabel } from '../lib/roles';
import { Home, FileText, Users, LogOut, FolderOpen, CheckSquare, HelpCircle, X, UserCircle, TrendingUp, History } from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
}

export default function Sidebar({ isOpen, onClose, onLogout }: SidebarProps) {
  const { user, userRole, userProfile } = useAuth();
  const { organization } = useOrganization();
  const location = useLocation();

  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const opener = document.activeElement as HTMLElement | null;
    const closeOnKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { onClose(); opener?.focus(); }
      if (event.key === 'Tab' && window.innerWidth < 1280) {
        const items = panel.current?.querySelectorAll<HTMLElement>('a[href],button:not([disabled])');
        if (!items?.length) return;
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    if (window.innerWidth < 1280) panel.current?.querySelector<HTMLElement>('button')?.focus();
    document.addEventListener('keydown', closeOnKey);
    return () => document.removeEventListener('keydown', closeOnKey);
  }, [isOpen, onClose]);
  if (!user) return null;

  const isActive = (path: string) => {
    return (location.pathname === path || (path === '/review' && location.pathname.startsWith('/review/'))) ? 'nav-active' : '';
  };

  const handleLinkClick = () => {
    if (window.innerWidth < 1280) onClose();
  };

  return (
    <>
      <div 
        className={`fixed inset-0 z-40 bg-slate-900/25 xl:hidden transition-opacity duration-200 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      
      <div ref={panel} id="workspace-navigation" aria-hidden={!isOpen} style={{visibility:isOpen ? 'visible' : 'hidden'}} className={`fixed left-0 top-0 flex h-full w-64 flex-col workspace-sidebar bg-white text-slate-700 z-50 transform transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex shrink-0 items-center justify-between px-4 pt-4 pb-3 border-b border-slate-200">
          <div className="flex min-w-0 items-center gap-2">
            {organization?.image_url && (
              <img src={organization.image_url} alt="" className="h-8 w-8 shrink-0 object-contain" />
            )}
            <h2 className="truncate font-semibold text-lg tracking-tight text-slate-900">{organization?.name || 'REPSE'}</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar navegación"
            className="p-2 hover:bg-slate-100 rounded transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav aria-label="Navegación principal" className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4">
          <div className="shrink-0 space-y-1">
          {userRole === 'normal' && (
            <Link
              to="/"
              onClick={handleLinkClick}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition ${isActive('/')}`}
            >
              <Home className="w-5 h-5" />
              Dashboard
            </Link>
          )}

          {userRole === 'normal' && (
            <>
              <Link
                to="/help"
                onClick={handleLinkClick}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition ${isActive('/help')}`}
              >
                <HelpCircle className="w-5 h-5" />
                Ayuda
              </Link>
            </>
          )}

          {(userRole === 'admin' || userRole === 'reviewer') && (
            <>
              <Link
                to="/admin/dashboard"
                onClick={handleLinkClick}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition ${isActive('/admin/dashboard')}`}
              >
                <Home className="w-5 h-5" />
                Resumen
              </Link>
              <Link
                to="/review"
                onClick={handleLinkClick}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition ${isActive('/review')}`}
              >
                <CheckSquare className="w-5 h-5" />
                Revisar
              </Link>
              <Link
                to="/admin/users"
                onClick={handleLinkClick}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition ${isActive('/admin/users')}`}
              >
                <Users className="w-5 h-5" />
                Usuarios
              </Link>

              <Link to="/admin/activity" onClick={handleLinkClick} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition ${isActive('/admin/activity')}`}><History className="w-5 h-5" />Actividad</Link>

              {userRole === 'admin' && (
                <>
                  <Link
                    to="/report"
                    onClick={handleLinkClick}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition ${isActive('/report')}`}
                  >
                    <TrendingUp className="w-5 h-5" />
                    Reporte
                  </Link>
                  
                  <Link
                    to="/admin/help"
                    onClick={handleLinkClick}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition ${isActive('/admin/help')}`}
                  >
                    <HelpCircle className="w-5 h-5" />
                    Guía Admin
                  </Link>
                </>
              )}
            </>
          )}

          </div>
          <div className="mt-auto shrink-0 space-y-1 border-t border-slate-200 pt-4">
            {userRole === 'admin' && <>
<Link
                    to="/admin/categories"
                    onClick={handleLinkClick}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition ${isActive('/admin/categories')}`}
                  >
                    <FolderOpen className="w-5 h-5" />
                    Categorías
                  </Link>
                  
                  <Link
                    to="/admin/definitions"
                    onClick={handleLinkClick}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition ${isActive('/admin/definitions')}`}
                  >
                    <FileText className="w-5 h-5" />
                    Definiciones
                  </Link>
            </>}
            <Link
              to="/profile"
              onClick={handleLinkClick}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition ${isActive('/profile')}`}
            >
              <UserCircle className="w-5 h-5" />
              Mi cuenta
            </Link>
            
            <button
              onClick={() => {
                onLogout();
                onClose();
              }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition w-full text-left"
            >
              <LogOut className="w-5 h-5" />
              Salir
            </button>
          </div>
        </nav>

        <div className="shrink-0 p-4 border-t border-slate-200">
          <div className="text-sm">
            {userProfile?.full_name && <div className="truncate font-medium">{formatPersonName(userProfile.full_name)}</div>}
            <div className={userProfile?.full_name ? 'break-all text-xs text-slate-500' : 'break-all font-medium'}>{user.email}</div>
            <div className="text-slate-500 text-xs">{roleLabel(userRole)}</div>
          </div>
        </div>
      </div>
    </>
  );
}